
export class ConversionResult {
    /**
     * A Conversion result that has both major and minor units, along with an indication
     * if the result is exact or not.
     * @param exact {boolean} if the result is exact
     * @param major {number} the major unit. EG feet
     * @param majorUnit {ConvertableUnit} the unit for the major part
     * @param minor {number} the minor unit. EG inches
     * @param minorUnit {ConvertableUnit} the minor unit
     */
    constructor(exact, major, majorUnit, minor, minorUnit) {
        this.exact = exact;
        this.major = major;
        this.minor = minor;
        this.majorUnit = majorUnit;
        this.minorUnit = minorUnit;
    }

    /**
     * Output a printable version of the conversion
     * @returns {string} the printable output
     */
    toPrintableOutput() {
        if(this.minorUnit) {
            let major = this.major.toFixed(0) + this.majorUnit.unitName;
            let minor = this.minor.toFixed(1) + this.minorUnit.unitName;
            return major + ", " + minor;
        } else {
            return this.major.toFixed(2) + this.majorUnit.unitName;
        }
    }
}

/**
 * A class that is the base of all units, that provides some general purpose unit conversion
 */
export class ConvertableUnit {
    /**
     * The allowable values within this unit
     * @type {[ConvertableUnit]}
     */
    static ENUM_VALUES = [];
    unitTypeName = "unknown";

    /**
     * A unit that can convert itself between internal form and a given unit. The
     * internal unit is a generally acecpted  unit that we can use such as meters,
     * litres, kilograms etc.
     * @param unitName {string} the generally accepted short name. E.G. kg
     * @param subUnit {ConvertableUnit} if there is a smaller unit, then this will be that smaller unit
     * @param internalRatio {number} the ratio between this and the internal unit.
     * @param printableName {string} printable name for the unit.
     */
    constructor(unitName, subUnit, internalRatio, printableName) {
        this.unitName = unitName;
        this.subUnit = subUnit;
        this.internalRatio = internalRatio;
        this.printableName = printableName;
        ConvertableUnit.ENUM_VALUES.push(this);
    }

    /**
     * Check if the other unit is compatible DIRECTLY with this one. EG both weights
     * or both heights
     * @param otherUnit the other unit to compare
     * @returns {boolean}
     */
    convertableTo(otherUnit) {
        return this.unitTypeName === otherUnit.unitTypeName;
    }

    /**
     * Converts a single amount in this unit to an internal measurement so that it can
     * be converted to another unit, or kept in the internal form.
     * @param amt the amount for the conversion in the given unit
     * @returns {number} in internal units
     */
    convertToInternal(amt) {
        return (amt * this.internalRatio);
    }

    /**
     * Converts a two part amount in this unit (for example feet and inches) to an internal
     * measurement so that it can be converted to another unit, or kept in the internal form.
     * @param amtMajor the major amount (eg feet)
     * @param amtMinor the minor amount (eg inches)
     * @returns {number} the internal form
     */
    fullConvertToInternal(amtMajor, amtMinor) {
        let maj = this.convertToInternal(amtMajor);
        let min = this.subUnit ? this.subUnit.convertToInternal(amtMinor) : 0.0;
        return maj + min;
    }

    /**
     * Converts an amount provided in internal units into this unit.
     * @param amt the amount in internal form
     * @returns {number}
     */
    convertFromInternal(amt) {
        return amt / this.internalRatio;
    }

    /**
     * Converts an amount provided in internal units into its output using a ConversionResult
     * to store the fully converted value, which will be in both major and minor units.
     *
     * @param amt the internal amount
     * @returns {ConversionResult} contains the coversion
     */
    fullConvertFromInternal(amt) {
        if(this.subUnit) {
            let amtConverted = this.convertFromInternal(amt);

            let subMeterRatio = this.subUnit.internalRatio;
            let detents = this.internalRatio / subMeterRatio;
            let wholeFract = (amtConverted - Math.floor(amtConverted)) * 100;
            let fract = (wholeFract / 100.0) * detents;
            return new ConversionResult(true, Math.floor(amtConverted), this, Math.round(fract), this.subUnit);
        } else {
            return new ConversionResult(true, this.convertFromInternal(amt), this, 0, null);
        }
    }
}

/**
 * An enumeration of possible weight units.
 */
export class WeightUnit extends ConvertableUnit {
    static GRAM = new WeightUnit('g', null, 0.001, "Gram");
    static KILOGRAM = new WeightUnit('kg', WeightUnit.GRAM, 1, "Kilogram");
    static OUNCE = new WeightUnit('oz', null, 0.028349523, "Ounce");
    static LB = new WeightUnit('lb', WeightUnit.OUNCE, 0.4535924, "Pound");
    static STONE = new WeightUnit('st', WeightUnit.LB, 6.3502932, "Stone");

    constructor(unitName, subUnit, kgRatio, printableName) {
        super(unitName, subUnit, kgRatio, printableName);
        this.unitTypeName = "weight";
    }
}

export class HeightUnit extends ConvertableUnit {
    static MILLIMETER = new HeightUnit("mm", null, 0.001, "Millimeter");
    static CENTIMETER = new HeightUnit("cm", HeightUnit.MILLIMETER, 0.01, "Centimeter");
    static METER = new HeightUnit("m", HeightUnit.CENTIMETER, 1, "Meter");
    static INCH = new HeightUnit("in", null, 0.0254, "Inch");
    static FEET = new HeightUnit("ft", HeightUnit.INCH, 0.3048, "Feet");
    static YARD = new HeightUnit("yd", HeightUnit.FEET, 0.9144, "Yard");

    constructor(unitName, subUnit, meterRatio, printableName) {
        super(unitName, subUnit, meterRatio, printableName);
        this.unitTypeName = "height";
    }
}

export class LiquidUnit extends ConvertableUnit {
    static MILLILITRE = new LiquidUnit("ml", null, 0.001, "Milli-litre");
    static CENTILITRE = new LiquidUnit("cl", LiquidUnit.MILLILITRE, 0.01, "Centi-litre");
    static LITRE = new LiquidUnit("l", LiquidUnit.MILLILITRE, 1, "Litre");

    static TBSP = new LiquidUnit("tbsp", null, 0.014787, "Tablespoon")
    static TSP = new LiquidUnit("tsp", null, 0.00492892, "Teaspoon")
    static CUP = new LiquidUnit("cup", null, 0.2365884, "Cup");
    static DRAM = new LiquidUnit("dram", null, 0.00369669, "Dram - US");
    static FLUIDOZ_US = new LiquidUnit("floz-us", LiquidUnit.DRAM, 0.02957353, "Fluid-Ounce - US");
    static PINT_US = new LiquidUnit("pint-us", LiquidUnit.FLUIDOZ_US, 0.473176, "Pint - US");
    static QUART_US = new LiquidUnit("quart-us", LiquidUnit.PINT_US, 0.946353, "Quart - US");
    static GALLON_US = new LiquidUnit("gallon-us", LiquidUnit.PINT_US, 3.785412, "Gallon - US");

    static FLUIDOZ_UK = new LiquidUnit("floz-uk", null, 0.0284131, "Fluid-Ounce - UK");
    static PINT_UK = new LiquidUnit("pint-uk", LiquidUnit.FLUIDOZ_UK, 0.568261, "Pint - UK");
    static QUART_UK = new LiquidUnit("quart-uk", LiquidUnit.PINT_UK, 1.13652, "Quart - UK");
    static GALLON_UK = new LiquidUnit("gallon-uk", LiquidUnit.PINT_UK, 4.54609, "Gallon - UK");

    constructor(unitName, subUnit, litreRatio, printableName) {
        super(unitName, subUnit, litreRatio, printableName);
        this.unitTypeName = "volume";
    }
}

/**
 * Gets the unit object from a text name
 * @param u the units name as text
 * @returns {ConvertableUnit}
 */
export function toUnit(u) {
    return ConvertableUnit.ENUM_VALUES.find(unit => (u === unit.unitName));
}

/**
 * Get the unit object for a height only given the unit name text
 * @param mode string representation of a unit
 * @return {WeightUnit}
 */
export function toWeightUnit(mode) {
    let prop =  toUnit(mode);
    return prop.unitTypeName === "weight" ? prop :  null;
}

/**
 * Get the unit object for a weight only given the unit name text
 * @param mode string representation of a unit
 * @return {HeightUnit}
 */
export function toHeightUnit(mode) {
    let prop =  toUnit(mode);
    return prop.unitTypeName === "height" ? prop : null;
}

/**
 * Get the unit object for a lquid measure only given the unit name text
 * @param mode string representation of a unit
 * @return {LiquidUnit}
 */
export function toLiquidUnit(mode) {
    let prop =  toUnit(mode);
    return prop.unitTypeName === "volume" ? prop : null;
}
